// =============================================================================
// AdminDash.jsx — staff/admin home page (the "All Requests" view)
// =============================================================================
// Renders: active banners, a multi-filter bar (status, shelter, search), and
//          a ReqTable showing every request. Supports preset filters passed
//          from elsewhere in the app (e.g. clicking a task jumps to its
//          shelter pre-filtered).
// Props:
//   onSelect               - row click handler
//   defaultShelterFilter   - optional preset filter applied on mount
//   onFilterApplied        - called once the preset filter has been consumed
// Context:
//   user                   - logged-in user (drives some visibility), from useSession
//   requests               - cached requests list, from useRequests
//   loadRequests           - reloads the list (used by auto-refresh), from useRequests
// =============================================================================

import React, { useState } from "react";
import { isInternalUser } from "../../app/access";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import { useRepeatCheckSettings } from "../../app/UiConfigContext.jsx";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useRepeatFlags } from "../../hooks/useRepeatFlags";
import { passesRepeatFilter, REPEAT_FILTERS, repeatReviewState } from "../../services/repeatCheck";
import { REQUEST_STATUSES, ROLES } from "../../utils/enums";
import { ALL_STATUSES } from "../../utils/formConstants";
import { Btn, Card } from "../../utils/ui.jsx";
import { ReqTable } from "./ReqTable.jsx";

function AdminDash({ onSelect, defaultShelterFilter, onFilterApplied }) {
  // `user` + `requests` + the list loader now come from context rather than props
  // (Phase 3 refactor).
  const { user } = useSession();
  const { requests, loadRequests } = useRequests();
  // Auto-refresh the request list so new/updated requests appear without a
  // manual reload. Polls every 30s while the tab is visible, and instantly when
  // the operator returns to the tab.
  useAutoRefresh(function () {
    loadRequests(user);
  });

  // Possible-repeat flags (db-211) for every fit-out request in the list, in ONE call that
  // re-runs only when the set of ids changes. Zephyr staff only; drafts and top-ups skipped.
  const repeatOn = isInternalUser(user);
  const repeatSettings = useRepeatCheckSettings();
  const repeatIds = (requests || [])
    .filter(function (r) {
      return r.status !== REQUEST_STATUSES.DRAFT && r.requestType !== "stock_top_up";
    })
    .map(function (r) {
      return r.id;
    });
  const { flags: repeatFlags } = useRepeatFlags(repeatIds, {
    enabled: repeatOn,
    months: repeatSettings.months,
    types: repeatSettings.types,
  });

  const [search, setSearch] = useState(defaultShelterFilter || "");
  const [fState, setFState] = useState("All");
  const [fStatus, setFStatus] = useState("All");
  const [fShelter, setFShelter] = useState("All");
  const [fCaseworker, setFCaseworker] = useState("All");
  const [fResponder, setFResponder] = useState("All");
  const [fApprovalLock, setFApprovalLock] = useState("All"); // All | Confirmed | Not confirmed
  const [fUpdated, setFUpdated] = useState("All"); // All | 24h | 7d | 30d
  const [fSubmittedFrom, setFSubmittedFrom] = useState("");
  const [fSubmittedTo, setFSubmittedTo] = useState("");
  const [fHasFees, setFHasFees] = useState(false);
  const [fHasUniforms, setFHasUniforms] = useState(false);
  const [fHasShoes, setFHasShoes] = useState(false);
  const [fHasDevice, setFHasDevice] = useState(false);
  const [fHasUnread, setFHasUnread] = useState(false);
  // Possible-repeat filter (db-211): All | flagged | not_reviewed | reviewed.
  const [fRepeat, setFRepeat] = useState("All");
  const [showMore, setShowMore] = useState(false);

  const STATES = ["All", "QLD", "WA", "NSW", "VIC", "SA", "TAS", "NT", "ACT"];
  const STATUSES = ["All", ...ALL_STATUSES];

  // Apply default filter when navigating from Users page
  React.useEffect(
    function () {
      if (defaultShelterFilter) {
        setSearch(defaultShelterFilter);
        if (onFilterApplied) onFilterApplied();
      }
    },
    [defaultShelterFilter]
  );

  // Build unique value lists for dropdowns — drafts excluded
  const nonDrafts = (requests || []).filter(function (r) {
    return r.status !== "Draft";
  });
  function uniq(arr) {
    const s = {};
    arr.forEach(function (v) {
      if (v) s[v] = true;
    });
    return Object.keys(s).sort();
  }
  const SHELTERS = [
    "All",
    ...uniq(
      nonDrafts.map(function (r) {
        return r.shelterName;
      })
    ),
  ];
  const CASEWORKERS = [
    "All",
    ...uniq(
      nonDrafts.map(function (r) {
        return r.contactName;
      })
    ),
  ];
  const RESPONDERS = [
    "All",
    ...uniq(
      nonDrafts
        .filter(function (r) {
          return r.updatedByRole === ROLES.STAFF;
        })
        .map(function (r) {
          return r.updatedBy;
        })
    ),
  ];

  function clearAll() {
    setSearch("");
    setFState("All");
    setFStatus("All");
    setFShelter("All");
    setFCaseworker("All");
    setFResponder("All");
    setFApprovalLock("All");
    setFUpdated("All");
    setFSubmittedFrom("");
    setFSubmittedTo("");
    setFHasFees(false);
    setFHasUniforms(false);
    setFHasShoes(false);
    setFHasDevice(false);
    setFHasUnread(false);
    setFRepeat("All");
  }

  function passesUpdatedFilter(r) {
    if (fUpdated === "All") return true;
    if (!r.updatedAt) return false;
    const diffH = (Date.now() - new Date(r.updatedAt).getTime()) / 36e5;
    if (fUpdated === "24h") return diffH <= 24;
    if (fUpdated === "7d") return diffH <= 24 * 7;
    if (fUpdated === "30d") return diffH <= 24 * 30;
    return true;
  }
  function passesSubmittedRange(r) {
    if (!fSubmittedFrom && !fSubmittedTo) return true;
    // Brisbane business day (reportDay), not the legacy UTC-baked `date`.
    const day = r.reportDay || r.date;
    if (!day) return false;
    if (fSubmittedFrom && day < fSubmittedFrom) return false;
    if (fSubmittedTo && day > fSubmittedTo) return false;
    return true;
  }
  function passesHasCategory(r) {
    if (!fHasFees && !fHasUniforms && !fHasShoes && !fHasDevice) return true;
    const kids = r.children || [];
    if (
      fHasFees &&
      !kids.some(function (c) {
        return (c.fees || []).length > 0;
      })
    )
      return false;
    if (
      fHasUniforms &&
      !kids.some(function (c) {
        return !!c.uniforms;
      })
    )
      return false;
    if (
      fHasShoes &&
      !kids.some(function (c) {
        return !!c.shoes;
      })
    )
      return false;
    if (
      fHasDevice &&
      !kids.some(function (c) {
        return !!c.laptop;
      })
    )
      return false;
    return true;
  }
  function passesUnread(r) {
    if (!fHasUnread) return true;
    const notes = r.notes || [];
    if (notes.length === 0) return false;
    const last = notes[notes.length - 1];
    const author = (last.author || "").toLowerCase();
    return (
      author.indexOf("zephyr") < 0 && author.indexOf("admin") < 0 && author.indexOf("responder") < 0
    );
  }

  const filtered = (requests || []).filter(function (r) {
    // Drafts belong to shelters and are hidden here by default; show them only
    // when the Draft status filter (dropdown or card) is active.
    if (r.status === REQUEST_STATUSES.DRAFT && fStatus !== REQUEST_STATUSES.DRAFT) return false;
    const q = search.toLowerCase();
    const ms =
      q === "" ||
      r.refNo.includes(search) ||
      r.shelterName.toLowerCase().includes(q) ||
      (r.shelterCode || "").toLowerCase().includes(q) ||
      (r.shelterRef || "").toLowerCase().includes(q) ||
      (r.contactName || "").toLowerCase().includes(q) ||
      // School: each child carries schoolName in the already-loaded children tree
      // (requests_view → get_request_children), so this needs no extra query.
      (r.children || []).some((c) => (c?.schoolName || "").toLowerCase().includes(q));
    if (!ms) return false;
    if (fState !== "All" && r.state !== fState) return false;
    if (fStatus !== "All" && r.status !== fStatus) return false;
    if (fShelter !== "All" && r.shelterName !== fShelter) return false;
    if (fCaseworker !== "All" && (r.contactName || "") !== fCaseworker) return false;
    if (fResponder !== "All" && (r.updatedBy || "") !== fResponder) return false;
    if (fApprovalLock === "Confirmed" && !r.approvalsConfirmedAt) return false;
    if (fApprovalLock === "Not confirmed" && r.approvalsConfirmedAt) return false;
    if (!passesUpdatedFilter(r)) return false;
    if (!passesSubmittedRange(r)) return false;
    if (!passesHasCategory(r)) return false;
    if (!passesUnread(r)) return false;
    if (fRepeat !== "All") {
      const st = repeatReviewState(repeatFlags[String(r.id)], r.approvalsConfirmedAt);
      if (!passesRepeatFilter(fRepeat, st)) return false;
    }
    return true;
  });

  // Pipeline status cards — click to filter the list by that status (click again
  // to clear). Counts come from ALL requests (drafts included) so drafts are
  // visible here even though the table hides them until the Draft card is active.
  const STATUS_CARDS = [
    { label: "Draft", status: REQUEST_STATUSES.DRAFT, colour: "#9E9E9E" },
    { label: "Submitted", status: REQUEST_STATUSES.SUBMITTED, colour: "#1E88E5" },
    { label: "Part Processed", status: REQUEST_STATUSES.PART_PROCESSED, colour: "#FB8C00" },
    { label: "Fully Processed", status: REQUEST_STATUSES.FULLY_PROCESSED, colour: "#2E7D32" },
  ];
  function countByStatus(st) {
    return (requests || []).filter(function (r) {
      return r.status === st;
    }).length;
  }
  // Average number of children per NON-DRAFT request, to 1 decimal place.
  const avgChildren =
    nonDrafts.length > 0
      ? (
          nonDrafts.reduce(function (sum, r) {
            return sum + (r.children || []).length;
          }, 0) / nonDrafts.length
        ).toFixed(1)
      : "0.0";

  const inputCls =
    "py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white cursor-pointer";
  const chkCls =
    "flex items-center gap-[5px] text-[12px] text-zephyr-text whitespace-nowrap cursor-pointer";

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="flex justify-between items-center mb-5">
        <h1 className="m-0 text-[22px] text-zephyr-text">All Requests</h1>
      </div>
      {/* Status cards — click to filter by that status (click again to clear) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5,1fr)",
          gap: 16,
          marginBottom: 18,
        }}
      >
        {STATUS_CARDS.map(function (c) {
          const active = fStatus === c.status;
          return (
            <button
              key={c.status}
              type="button"
              aria-label={`Filter by ${c.label}`}
              onClick={function () {
                setFStatus(active ? "All" : c.status);
              }}
              style={{
                background: "white",
                borderRadius: 10,
                padding: "10px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                border: active ? `2px solid ${c.colour}` : "2px solid transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                width: "100%",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
              >
                <div style={{ fontSize: 36, fontWeight: 700, color: c.colour }}>
                  {countByStatus(c.status)}
                </div>
                <div
                  style={{ fontSize: 20, color: "#607D79", lineHeight: 1.1, textAlign: "center" }}
                >
                  {c.label.split(" ").map(function (w) {
                    return <div key={w}>{w}</div>;
                  })}
                </div>
              </div>
            </button>
          );
        })}
        {/* Average children — display-only metric (non-draft requests) */}
        <div
          style={{
            background: "white",
            borderRadius: 10,
            padding: "10px 20px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
            border: "2px solid transparent",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: "#00897B" }}>{avgChildren}</div>
            <div style={{ fontSize: 20, color: "#607D79" }}>Average Children</div>
          </div>
        </div>
      </div>
      <Card style={{ marginBottom: 18, padding: 16 }}>
        {/* Row 1 — search + main dropdowns + toggle */}
        <div className="flex gap-[10px] flex-wrap items-center">
          <input
            placeholder="Search ref, DFV Service, caseworker, school..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={inputCls}
            style={{ width: 240, fontFamily: "inherit" }}
          />
          <select
            value={fState}
            onChange={(e) => setFState(e.target.value)}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
            title="State"
          >
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s === "All" ? "All states" : s}
              </option>
            ))}
          </select>
          <select
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
            title="Status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === "All" ? "All statuses" : s}
              </option>
            ))}
          </select>
          <select
            value={fShelter}
            onChange={(e) => setFShelter(e.target.value)}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
            title="DFV Service"
          >
            {SHELTERS.map((s) => (
              <option key={s} value={s}>
                {s === "All" ? "All DFV Services" : s}
              </option>
            ))}
          </select>
          <select
            value={fCaseworker}
            onChange={(e) => setFCaseworker(e.target.value)}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
            title="Caseworker"
          >
            {CASEWORKERS.map((s) => (
              <option key={s} value={s}>
                {s === "All" ? "All caseworkers" : s}
              </option>
            ))}
          </select>
          <select
            id="repeat-filter"
            value={fRepeat}
            onChange={(e) => setFRepeat(e.target.value)}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
            title="Possible repeats"
            aria-label="Possible repeats"
          >
            {REPEAT_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Btn variant="secondary" onClick={() => setShowMore(!showMore)}>
            {showMore ? "Hide filters" : "More filters"}
          </Btn>
          <Btn variant="secondary" onClick={clearAll}>
            Clear
          </Btn>
        </div>

        {/* Row 2 — extra filters (toggleable) */}
        {showMore && (
          <div className="mt-3 pt-3 border-t border-dashed border-zephyr-border flex gap-[10px] flex-wrap items-center">
            <select
              value={fResponder}
              onChange={(e) => setFResponder(e.target.value)}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
              title="Responder"
            >
              {RESPONDERS.map((s) => (
                <option key={s} value={s}>
                  {s === "All" ? "All responders" : s}
                </option>
              ))}
            </select>
            <select
              value={fApprovalLock}
              onChange={(e) => setFApprovalLock(e.target.value)}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
              title="Approval lock"
            >
              <option value="All">Approval: any</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Not confirmed">Not confirmed</option>
            </select>
            <select
              value={fUpdated}
              onChange={(e) => setFUpdated(e.target.value)}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
              title="Last updated"
            >
              <option value="All">Updated: any</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <label className="text-[12px] text-zephyr-muted flex items-center gap-[6px]">
              Submitted:
              <input
                type="date"
                value={fSubmittedFrom}
                onChange={(e) => setFSubmittedFrom(e.target.value)}
                className={inputCls}
                style={{ padding: "6px 10px", fontFamily: "inherit" }}
              />
              <span>–</span>
              <input
                type="date"
                value={fSubmittedTo}
                onChange={(e) => setFSubmittedTo(e.target.value)}
                className={inputCls}
                style={{ padding: "6px 10px", fontFamily: "inherit" }}
              />
            </label>
            <span className="text-[12px] text-zephyr-muted">Has:</span>
            <label className={chkCls}>
              <input
                type="checkbox"
                checked={fHasFees}
                onChange={(e) => setFHasFees(e.target.checked)}
              />{" "}
              Fees
            </label>
            <label className={chkCls}>
              <input
                type="checkbox"
                checked={fHasUniforms}
                onChange={(e) => setFHasUniforms(e.target.checked)}
              />{" "}
              Uniforms
            </label>
            <label className={chkCls}>
              <input
                type="checkbox"
                checked={fHasShoes}
                onChange={(e) => setFHasShoes(e.target.checked)}
              />{" "}
              Shoes
            </label>
            <label className={chkCls}>
              <input
                type="checkbox"
                checked={fHasDevice}
                onChange={(e) => setFHasDevice(e.target.checked)}
              />{" "}
              Device
            </label>
            <label className={chkCls}>
              <input
                type="checkbox"
                checked={fHasUnread}
                onChange={(e) => setFHasUnread(e.target.checked)}
              />{" "}
              Unread shelter comments
            </label>
          </div>
        )}

        <div className="mt-2 text-[13px] text-zephyr-muted">
          Showing {filtered.length} of {(requests || []).length} requests
        </div>
      </Card>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <ReqTable
          requests={filtered}
          onSelect={onSelect}
          showShelter={true}
          repeatFlags={repeatOn ? repeatFlags : null}
        />
      </Card>
    </div>
  );
}

export { AdminDash };
